/**
 * NotificationProvider — Fixed version
 *
 * BUGS FIXED:
 * 1. showNotificationGlobal export was a stale closure snapshot — callers outside React
 *    would always hold the initial no-op function, so notifications never showed.
 *    Fixed by exporting a stable OBJECT reference ({ fn }) and mutating only the inner
 *    property. Importers call showNotificationGlobal.fn(...) OR use the convenience
 *    wrapper exported below.
 *
 * 2. Pending-queue flush ran synchronously inside useLayoutEffect — React 18 batching
 *    could silently drop some state updates. Fixed with individual setTimeout(,0) per
 *    queued notification so each lands in its own render cycle.
 *
 * 3. No dismiss animation — notifications disappeared instantly. Fixed with a CSS
 *    class-based slide-out animation and an animationend listener that removes the DOM
 *    node only after the animation completes.
 *
 * 4. Progress bar couldn't pause on hover — bar was driven by a plain CSS animation
 *    with no way to resume at the exact remaining width. Fixed by reading computed
 *    width on mouseleave and restarting the animation for just the remaining duration,
 *    while also resetting the JS auto-dismiss timer to match.
 *
 * 5. Icon sizing was unconstrained — inherited the parent font-size and rendered
 *    oversized. Fixed with explicit width/height on the icon wrapper.
 */

import React, {
  createContext,
  useContext,
  useState,
  useCallback,
  useLayoutEffect,
  useRef,
} from 'react';
import { SiTicktick } from 'react-icons/si';
import { MdErrorOutline } from 'react-icons/md';
import { FiInfo } from 'react-icons/fi';
import { BiSolidError } from 'react-icons/bi';

/* ─────────────────────────────────────────────
   GLOBAL REFERENCE — stable object, mutable fn
   Import { showNotification } from this file to
   call notifications from anywhere (non-React).
───────────────────────────────────────────── */
let _pendingQueue = [];

/**
 * Stable object whose `.fn` property is swapped when the provider mounts.
 * Exporting an object (not a bare `let`) means every importer always reads
 * the CURRENT function, not a stale closure captured at import time.
 */
const _globalRef = {
  fn: (...args) => {
    _pendingQueue.push(args);
  },
};

/**
 * Call this anywhere — inside or outside React components.
 * @param {string} message
 * @param {'info'|'success'|'error'|'warning'} type
 * @param {'topLeft'|'topCenter'|'topRight'|'bottomLeft'|'bottomCenter'|'bottomRight'} placement
 */
export const showNotification = (...args) => _globalRef.fn(...args);

/* ─────────────────────────────────────────────
   CONSTANTS
───────────────────────────────────────────── */
const AUTO_DISMISS_MS = 4000;

const ICONS = {
  success: <SiTicktick />,
  error:   <MdErrorOutline />,
  info:    <FiInfo />,
  warning: <BiSolidError />,
};

const THEMES = {
  success: {
    bg:         '#ebfbee',
    border:     '#8ce99a',
    color:      '#1b5e20',
    iconBg:     '#8ce99a',
    barColor:   '#37b24d',
    darkBg:     '#1b3a1f',
    darkBorder: '#2b6d32',
    darkColor:  '#a5d6a7',
    darkIconBg: '#2b6d32',
    darkBar:    '#66bb6a',
  },
  error: {
    bg:         '#fff5f5',
    border:     '#ffa8a8',
    color:      '#7f0000',
    iconBg:     '#ffa8a8',
    barColor:   '#e03131',
    darkBg:     '#3b1a1a',
    darkBorder: '#7f2020',
    darkColor:  '#ffcdd2',
    darkIconBg: '#7f2020',
    darkBar:    '#ef9a9a',
  },
  info: {
    bg:         '#e7f5ff',
    border:     '#a5d8ff',
    color:      '#0d3b6e',
    iconBg:     '#a5d8ff',
    barColor:   '#1971c2',
    darkBg:     '#0d2540',
    darkBorder: '#1a4a80',
    darkColor:  '#90caf9',
    darkIconBg: '#1a4a80',
    darkBar:    '#64b5f6',
  },
  warning: {
    bg:         '#fff9db',
    border:     '#ffe066',
    color:      '#7d4e00',
    iconBg:     '#ffe066',
    barColor:   '#e67700',
    darkBg:     '#3b2c00',
    darkBorder: '#7d5800',
    darkColor:  '#ffe082',
    darkIconBg: '#7d5800',
    darkBar:    '#ffca28',
  },
};

const PLACEMENT_STYLES = {
  topLeft:      { top: 16,  left: 16,               alignItems: 'flex-start' },
  topCenter:    { top: 16,  left: '50%',             alignItems: 'center',   transform: 'translateX(-50%)' },
  topRight:     { top: 16,  right: 16,               alignItems: 'flex-end' },
  bottomLeft:   { bottom: 16, left: 16,              alignItems: 'flex-start' },
  bottomCenter: { bottom: 16, left: '50%',           alignItems: 'center',   transform: 'translateX(-50%)' },
  bottomRight:  { bottom: 16, right: 16,             alignItems: 'flex-end' },
};

/* ─────────────────────────────────────────────
   CONTEXT
───────────────────────────────────────────── */
const NotificationContext = createContext(null);

/* ─────────────────────────────────────────────
   SINGLE NOTIFICATION ITEM
───────────────────────────────────────────── */
function NotificationItem({ notification, onRemove }) {
  const { id, message, type, placement } = notification;
  const theme   = THEMES[type] ?? THEMES.info;
  const isBottom = placement.startsWith('bottom');
  const barRef   = useRef(null);
  const timerRef = useRef(null);
  const elRef    = useRef(null);

  // Start the auto-dismiss timer
  const startTimer = useCallback((duration = AUTO_DISMISS_MS) => {
    clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => triggerLeave(), duration);
  }, []); // eslint-disable-line

  // Animate out then notify parent to remove from state
  const triggerLeave = useCallback(() => {
    const el = elRef.current;
    if (!el || el.dataset.leaving) return;
    el.dataset.leaving = 'true';
    el.style.animation = 'notifSlideOut 0.22s ease forwards';
    el.addEventListener('animationend', () => onRemove(id), { once: true });
  }, [id, onRemove]);

  // Pause progress bar and timer on hover
  const handleMouseEnter = useCallback(() => {
    clearTimeout(timerRef.current);
    if (barRef.current) barRef.current.style.animationPlayState = 'paused';
  }, []);

  // Resume from the exact remaining width
  const handleMouseLeave = useCallback(() => {
    const bar = barRef.current;
    const el  = elRef.current;
    if (!bar || !el) return;

    const remaining = (parseFloat(getComputedStyle(bar).width) / el.offsetWidth) * AUTO_DISMISS_MS;
    bar.style.animation = 'none';
    // Force reflow so the browser registers the reset before restarting
    bar.getBoundingClientRect();
    bar.style.animation = `notifProgress ${remaining}ms linear forwards`;
    startTimer(remaining);
  }, [startTimer]);

  useLayoutEffect(() => {
    startTimer();
    return () => clearTimeout(timerRef.current);
  }, [startTimer]);

  const entryAnimation = isBottom ? 'notifSlideInBottom' : 'notifSlideInTop';

  return (
    <div
      ref={elRef}
      role="alert"
      aria-live="polite"
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      style={{
        position:    'relative',
        display:     'flex',
        alignItems:  'center',
        gap:         12,
        padding:     '12px 14px',
        minWidth:    280,
        maxWidth:    380,
        borderRadius: 12,
        border:      `0.5px solid ${theme.border}`,
        background:  theme.bg,
        color:       theme.color,
        overflow:    'hidden',
        pointerEvents: 'auto',
        animation:   `${entryAnimation} 0.28s cubic-bezier(0.34,1.56,0.64,1) forwards`,
      }}
    >
      {/* Icon */}
      <div style={{
        width:           30,
        height:          30,
        borderRadius:    '50%',
        background:      theme.iconBg,
        display:         'flex',
        alignItems:      'center',
        justifyContent:  'center',
        fontSize:        14,
        flexShrink:      0,
      }}>
        {ICONS[type] ?? ICONS.info}
      </div>

      {/* Message */}
      <div style={{ flex: 1, fontSize: 13, fontWeight: 500, lineHeight: 1.5, minWidth: 0 }}>
        {message}
      </div>

      {/* Close */}
      <button
        onClick={triggerLeave}
        aria-label="Dismiss"
        style={{
          background:   'transparent',
          border:       'none',
          cursor:       'pointer',
          color:        'inherit',
          opacity:      0.55,
          fontSize:     18,
          lineHeight:   1,
          padding:      '2px 4px',
          borderRadius: 4,
          flexShrink:   0,
          transition:   'opacity 0.15s',
        }}
        onMouseEnter={e => (e.currentTarget.style.opacity = 1)}
        onMouseLeave={e => (e.currentTarget.style.opacity = 0.55)}
      >
        ×
      </button>

      {/* Progress bar */}
      <div
        ref={barRef}
        style={{
          position:  'absolute',
          bottom:    0,
          left:      0,
          height:    2,
          background: theme.barColor,
          animation: `notifProgress ${AUTO_DISMISS_MS}ms linear forwards`,
        }}
      />
    </div>
  );
}

/* ─────────────────────────────────────────────
   PROVIDER
───────────────────────────────────────────── */
export const NotificationProvider = ({ children }) => {
  const [notifications, setNotifications] = useState([]);

  const addNotification = useCallback((message, type = 'info', placement = 'topCenter') => {
    const id = `${Date.now()}-${Math.random()}`;
    setNotifications(prev => [...prev, { id, message, type, placement }]);
  }, []);

  const removeNotification = useCallback((id) => {
    setNotifications(prev => prev.filter(n => n.id !== id));
  }, []);

  // ── Wire up global reference ──────────────────────────────────────────
  // Using useLayoutEffect means _globalRef.fn is updated BEFORE any child
  // renders, so even synchronous calls during mount are captured correctly.
  useLayoutEffect(() => {
    _globalRef.fn = addNotification;

    // Flush any notifications queued before the provider mounted.
    // Each one gets its own setTimeout(,0) so React processes them as
    // separate state updates rather than batching them into one.
    const queued = _pendingQueue.splice(0);
    queued.forEach(args => setTimeout(() => addNotification(...args), 0));

    return () => {
      // Restore the queuing function so calls during unmount don't get lost
      _globalRef.fn = (...args) => { _pendingQueue.push(args); };
    };
  }, [addNotification]);

  // Group by placement
  const byPlacement = notifications.reduce((acc, n) => {
    const key = n.placement ?? 'topCenter';
    (acc[key] ??= []).push(n);
    return acc;
  }, {});

  return (
    <NotificationContext.Provider value={addNotification}>
      {children}

      {Object.entries(byPlacement).map(([pos, notifs]) => (
        <div
          key={pos}
          style={{
            position:      'fixed',
            zIndex:        9999,
            display:       'flex',
            flexDirection: 'column',
            gap:           10,
            pointerEvents: 'none',
            ...PLACEMENT_STYLES[pos],
          }}
        >
          {notifs.map(n => (
            <NotificationItem
              key={n.id}
              notification={n}
              onRemove={removeNotification}
            />
          ))}
        </div>
      ))}

      <style>{`
        @keyframes notifSlideInTop {
          from { opacity: 0; transform: translateY(-12px) scale(0.97); }
          to   { opacity: 1; transform: translateY(0)     scale(1);    }
        }
        @keyframes notifSlideInBottom {
          from { opacity: 0; transform: translateY(12px)  scale(0.97); }
          to   { opacity: 1; transform: translateY(0)     scale(1);    }
        }
        @keyframes notifSlideOut {
          from { opacity: 1; transform: scale(1);    max-height: 80px; }
          to   { opacity: 0; transform: scale(0.95); max-height: 0;    padding: 0; }
        }
        @keyframes notifProgress {
          from { width: 100%; }
          to   { width: 0%;   }
        }
      `}</style>
    </NotificationContext.Provider>
  );
};

/* ─────────────────────────────────────────────
   HOOK — for use inside React components
───────────────────────────────────────────── */
export const useNotification = () => {
  const ctx = useContext(NotificationContext);
  if (!ctx) throw new Error('useNotification must be used within a NotificationProvider');
  return ctx;
};

// Backward-compatible alias so old imports don't break
export { showNotification as showNotificationGlobal };